import { ArrowLeft, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import ContraindicationsModal from "../../../component/therapy-sections/ContraindicationsModal";
import useCameraRecorder from "../../../hooks/useCameraRecorder";
import {
  GET_SIGNED_URL,
  submitExercise,
} from "../../../api/exercises/exercises";
import toast from "react-hot-toast";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import useMutation from "../../../hooks/useMutattion";
import { HttpStatusCode } from "axios";
import useIsLandscape from "../../../hooks/useIsLandscape";
import { useWindowSize } from "../../../hooks/useWindowSize";
import LandscapeModeUI from "../../../share/ui/LandscapeModeUI";

const TABLET = 1024;

const ExerciseDetail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { "*": exerciseId } = useParams();

  const exercise = useMemo(() => {
    if (location.state) {
      return location.state;
    }
    if (exerciseId) {
      const storedExercise = localStorage.getItem(`exercise_${exerciseId}`);
      if (storedExercise) {
        try {
          return JSON.parse(storedExercise);
        } catch (e) {
          console.error("Failed to parse exercise from localStorage", e);
        }
      }
    }
    return null;
  }, [location.state, exerciseId]);
  const videoCapture = exercise?.videoCapture || "";
  const countSets = exercise?.countSets || 0;
  const attributeTags = exercise?.attributeTags;
  const videoTypeTag =
    attributeTags &&
    attributeTags.video_type &&
    Array.isArray(attributeTags.video_type)
      ? attributeTags.video_type[0]
      : null;
  const videoType = videoTypeTag?.value?.en || "";
  const isDurationMode = videoType === "ExerciseA" || videoType === "ExerciseB";
  const timeVideoExercise = exercise?.timeVideoExercise || 0;
  const countReps = isDurationMode
    ? timeVideoExercise
    : exercise?.countReps || 0;
  const timeBetweenSets = exercise?.timeBetweenSets || 0;
  const [currentSet, setCurrentSet] = useState(1);
  const [currentRep, setCurrentRep] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [restProgress, setRestProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [trimmedVideoBlobUrls, setTrimmedVideoBlobUrls] = useState<string[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);
  const currentLang =
    typeof window !== "undefined"
      ? localStorage.getItem("i18nextLng") || "en"
      : "en";
  let matchedContent = exercise?.contents?.find(
    (content: any) => content.languageCode === currentLang
  );
  if (!matchedContent && Array.isArray(exercise?.contents)) {
    matchedContent = exercise.contents.find(
      (content: any) => !!content && !!content.name
    );
  }
  const [step, setStep] = useState<"init" | "preview" | "recording">("init");
  const [contraModalOpen, setContraModalOpen] = useState(false);
  const [isContraScrolledToEnd, setIsContraScrolledToEnd] = useState(false);
  const [isContraScrollable, setIsContraScrollable] = useState(false);
  const contraDialogContentRef = useRef<HTMLDivElement>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ffmpegRef = useRef(new FFmpeg());
  const ffmpegLoaded = useRef(false);
  const isMobile = useWindowSize(TABLET);
  const isLandscape = useIsLandscape();
  // Store start time in UTC when user clicks startExercise
  const [exerciseStartTime, setExerciseStartTime] = useState<string | null>(
    null
  );
  const [exerciseEndTime, setExerciseEndTime] = useState<string | null>(null);

  interface ISignedUrlResponse {
    url: string;
    fileName: string;
    filePath: string;
    expiresAt: string;
  }

  const loadFFmpeg = async () => {
    if (ffmpegLoaded.current) return;
    setIsProcessingVideo(true);
    toast.loading(t("toast.preparingVideoTool"), {
      id: "ffmpeg-load",
    });
    const ffmpeg = ffmpegRef.current;
    const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(
        `${baseURL}/ffmpeg-core.wasm`,
        "application/wasm"
      ),
    });
    ffmpegLoaded.current = true;
    setIsProcessingVideo(false);
    toast.success(t("toast.toolReady"), {
      id: "ffmpeg-load",
    });
  };

  const { execute: getSignedUrl } = useMutation<ISignedUrlResponse>(
    GET_SIGNED_URL,
    "POST",
    { autoFetch: false }
  );

  const {
    stream,
    startPreview,
    startRecording,
    stopRecording,
    recordedVideoUrl,
    resetRecordedVideoUrl,
    isRecording,
    error,
  } = useCameraRecorder();

  const playVideo = async (video: HTMLVideoElement | null) => {
    if (!video) return;
    try {
      if (video.paused) {
        await video.play();
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        console.error("Error playing video:", err);
      }
    }
  };

  const pauseVideo = (video: HTMLVideoElement | null) => {
    if (video && !video.paused) {
      video.pause();
    }
  };

  // Audio playback logic
  const audioRef = useRef<HTMLAudioElement>(null);
  // playAudio and pauseAudio removed (no-unused-vars)
  const handleStartPreview = async () => {
    // If contraindications exist, show modal first
    if (
      Array.isArray(exercise?.contraindications) &&
      exercise.contraindications.length > 0
    ) {
      setContraModalOpen(true);
      return;
    }
    setStep("preview");
    await startPreview();
  };

  // Handler for Agree in contraindications modal
  const handleContraAgree = async () => {
    setContraModalOpen(false);
    setStep("preview");
    await startPreview();
    setIsContraScrolledToEnd(false);
  };

  // Handler for Disagree in contraindications modal
  const handleContraDisagree = () => {
    setContraModalOpen(false);
    setIsContraScrolledToEnd(false);
  };
  // Handler for scroll in contraindications modal
  const handleContraScroll = () => {
    const el = contraDialogContentRef.current;
    if (!el) return;
    if (!isContraScrollable) return;
    // Allow a small threshold for floating point errors
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
      setIsContraScrolledToEnd(true);
    } else {
      setIsContraScrolledToEnd(false);
    }
  };

  // Check if contraindications content is scrollable when modal opens
  useEffect(() => {
    if (contraModalOpen) {
      setTimeout(() => {
        const el = contraDialogContentRef.current;
        if (el) {
          if (el.scrollHeight > el.clientHeight + 2) {
            setIsContraScrollable(true);
            setIsContraScrolledToEnd(false);
          } else {
            setIsContraScrollable(false);
            setIsContraScrolledToEnd(true);
          }
        }
      }, 100); // Wait for content to render
    } else {
      setIsContraScrollable(false);
      setIsContraScrolledToEnd(false);
    }
  }, [contraModalOpen, exercise?.contraindications]);

  useEffect(() => {
    if (step !== "recording" || !isRecording) return;

    const attributeTags = exercise?.attributeTags;
    const videoTypeTag =
      attributeTags &&
      attributeTags.video_type &&
      Array.isArray(attributeTags.video_type)
        ? attributeTags.video_type[0]
        : null;
    const videoType = videoTypeTag?.value?.en || "";
    const isDurationMode =
      videoType === "ExerciseA" || videoType === "ExerciseB";
    const isRepetitionMode = videoType === "Repetition";
    const timeVideoExercise = exercise?.timeVideoExercise || 0;
    let rep = 0;
    let set = 1;
    let repInterval: NodeJS.Timeout | undefined;
    let restInterval: NodeJS.Timeout | undefined;
    let durationProgress = 0;
    let durationInterval: NodeJS.Timeout | undefined;

    const startRepetitionCycle = () => {
      if (isDurationMode) {
        // Duration mode: rep increases from 1 to timeVideoExercise, progress bar follows rep
        rep = 0;
        setCurrentRep(rep);
        repInterval = setInterval(() => {
          if (rep < timeVideoExercise) {
            rep++;
            setCurrentRep(rep);
            setRestProgress((rep / timeVideoExercise) * 100);
          } else {
            clearInterval(repInterval);
            if (set < countSets) {
              if (videoRef.current) {
                pauseVideo(videoRef.current);
                videoRef.current.currentTime = 0;
              }
              setIsResting(true);
              setRestProgress(0);
              let elapsed = 0;
              restInterval = setInterval(() => {
                elapsed++;
                setRestProgress((elapsed / timeBetweenSets) * 100);
                if (elapsed >= timeBetweenSets + 1) {
                  clearInterval(restInterval);
                  set++;
                  setCurrentSet(set);
                  setIsResting(false);
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                    playVideo(videoRef.current);
                  }
                  startRepetitionCycle();
                }
              }, 1000);
            } else {
              setIsDone(true);
            }
          }
        }, 1000);
      } else if (isRepetitionMode) {
        // Repetition mode: rep increases after each timeVideoExercise
        durationProgress = 0;
        durationInterval = setInterval(() => {
          durationProgress++;
          setRestProgress((durationProgress / timeVideoExercise) * 100);
          if (durationProgress >= timeVideoExercise) {
            clearInterval(durationInterval);
            rep++;
            setCurrentRep(rep);
            if (rep < countReps) {
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
                playVideo(videoRef.current);
              }
              startRepetitionCycle();
            } else {
              if (set < countSets) {
                if (videoRef.current) {
                  pauseVideo(videoRef.current);
                  videoRef.current.currentTime = 0;
                }
                setIsResting(true);
                setRestProgress(0);
                let elapsed = 0;
                restInterval = setInterval(() => {
                  elapsed++;
                  setRestProgress((elapsed / timeBetweenSets) * 100);
                  if (elapsed >= timeBetweenSets + 1) {
                    clearInterval(restInterval);
                    set++;
                    rep = 0;
                    setCurrentSet(set);
                    setCurrentRep(rep);
                    setIsResting(false);
                    if (videoRef.current) {
                      videoRef.current.currentTime = 0;
                      playVideo(videoRef.current);
                    }
                    startRepetitionCycle();
                  }
                }, 1000);
              } else {
                setIsDone(true);
              }
            }
          }
        }, 1000);
      } else {
        // Default: rep increases every second
        repInterval = setInterval(() => {
          if (rep < countReps) {
            rep++;
            setCurrentRep(rep);
          } else {
            clearInterval(repInterval);
            if (set < countSets) {
              if (videoRef.current) {
                pauseVideo(videoRef.current);
                videoRef.current.currentTime = 0;
              }
              setIsResting(true);
              setRestProgress(0);
              let elapsed = 0;
              restInterval = setInterval(() => {
                elapsed++;
                setRestProgress((elapsed / timeBetweenSets) * 100);
                if (elapsed >= timeBetweenSets + 1) {
                  clearInterval(restInterval);
                  set++;
                  rep = 0;
                  setCurrentSet(set);
                  setCurrentRep(rep);
                  setIsResting(false);
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                    playVideo(videoRef.current);
                  }
                  startRepetitionCycle();
                }
              }, 1000);
            } else {
              setIsDone(true);
            }
          }
        }, 1000);
      }
    };

    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      playVideo(videoRef.current);
    }
    startRepetitionCycle();

    return () => {
      if (repInterval) clearInterval(repInterval);
      if (restInterval) clearInterval(restInterval);
      if (durationInterval) clearInterval(durationInterval);
    };
  }, [step, isRecording, exercise, countReps, countSets, timeBetweenSets]);

  const handleStartRecording = async () => {
    // Get current time in UTC ISO string
    const nowUtc = new Date();
    setExerciseStartTime(nowUtc.toISOString());
    const prepTime = exercise?.config?.preparationTime || 5;
    setCountdown(prepTime);
    let count = prepTime;
    const interval = setInterval(() => {
      count -= 1;
      setCountdown(count);
      if (count === 0) {
        clearInterval(interval);
        setCountdown(null);
        setStep("recording");
        startRecording();
      }
    }, 1000);
  };

  const handleRestart = async () => {
    await stopRecording();
    resetRecordedVideoUrl();
    cleanupStream();

    if (videoRef.current) {
      pauseVideo(videoRef.current);
      videoRef.current.currentTime = 0;
    }

    setCurrentSet(1);
    setCurrentRep(0);
    setIsResting(false);
    setRestProgress(0);
    setIsDone(false);
    setStep("init");
  };

  const handleBack = async () => {
    if (isRecording || stream) {
      await stopRecording();
      cleanupStream();
    }
    navigate("/exercise-list");
  };

  const uploadAndGetUrl = async (file: File): Promise<string | null> => {
    try {
      const res = await getSignedUrl({ contentType: file.type });
      if (!res?.url) {
        toast.error(`${t("toast.failedToGetUploadUrl")} for ${file.name}`);
        return null;
      }
      const uploadResponse = await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (uploadResponse.status !== HttpStatusCode.Ok) {
        toast.error(`${t("toast.uploadFailed")} for ${file.name}`);
        return null;
      }
      const url = new URL(res.url);
      const pathName = url.pathname.split("/");
      return `${url.origin}/${pathName[1]}/${res.filePath}`;
    } catch (err) {
      console.error(`Upload error for ${file.name}:`, err);
      toast.error(`${t("toast.uploadFailed")} for ${file.name}`);
      return null;
    }
  };

  const handleFinish = async () => {
    setIsUploading(true);
    try {
      await stopRecording();
      cleanupStream();
      if (videoFiles.length === 0) {
        toast.error(t("title.noVideoToSubmit"));
        return;
      }
      const uploadPromises = videoFiles.map(uploadAndGetUrl);
      const urls = await Promise.all(uploadPromises);
      const successfulUrls = urls.filter((url): url is string => url !== null);
      if (successfulUrls.length !== videoFiles.length) {
        toast.error(t("toast.someUploadsFailed"), { id: "upload-videos" });
        return;
      }

      if (
        exercise?.therapyItemType === "exercise" &&
        exercise?.referItems === "feedback"
      ) {
        navigate(`/exercise/feedback/${exercise.id}`, {
          state: {
            exercise: exercise,
            planId: exercise?.id,
            videoUrl: successfulUrls,
            startTime: exerciseStartTime ?? undefined,
            endTime: exerciseEndTime ?? undefined,
          },
        });
      } else {
        await submitExercise({
          planId: exercise?.id,
          videoUrl: successfulUrls,
          startTime: exerciseStartTime ?? undefined,
          endTime: exerciseEndTime ?? undefined,
        });
        toast.success(t("title.uploadSuccess"), { id: "upload-videos" });
        navigate("/exercise-list");
      }
    } catch (err) {
      console.error("Error submitting exercise:", err);
      toast.error(t("toast.submissionFailed"), { id: "upload-videos" });
    } finally {
      setIsUploading(false);
    }
  };
  useEffect(() => {
    if (!exercise) {
      navigate("/exercise-list");
    }
  }, [exercise, navigate, t]);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  useEffect(() => {
    // Cleanup stream on component unmount
    return () => cleanupStream();
  }, []);

  useEffect(() => {
    if (isDone && !exerciseEndTime) {
      if (videoRef.current) {
        pauseVideo(videoRef.current);
        videoRef.current.currentTime = 0;
      }
      stopRecording();
      cleanupStream();
      const nowUtc = new Date();
      setExerciseEndTime(nowUtc.toISOString());
    }
  }, [isDone, stopRecording, exerciseEndTime]);

  useEffect(() => {
    if (step === "preview" && previewVideoRef.current && stream) {
      previewVideoRef.current.srcObject = stream;
    }
    if (step === "recording" && cameraVideoRef.current && stream) {
      cameraVideoRef.current.srcObject = stream;
    }
  }, [step, stream]);

  useEffect(() => {
    let sizeCheckInterval: NodeJS.Timeout | null = null;
    if (isRecording && recordedVideoUrl) {
      sizeCheckInterval = setInterval(async () => {
        try {
          const response = await fetch(recordedVideoUrl);
          const blob = await response.blob();
          const sizeMB = blob.size / (1024 * 1024);
          if (sizeMB >= 99) {
            await stopRecording();
          }
        } catch (err) {
          console.error("Error checking video size:", err);
        }
      }, 1000);
    }
    return () => {
      if (sizeCheckInterval) clearInterval(sizeCheckInterval);
    };
  }, [isRecording, recordedVideoUrl, stopRecording]);

  useEffect(() => {
    if (step === "init" && videoRef.current) {
      // Set src only if changed
      if (
        audioRef.current &&
        audioRef.current.src !== (matchedContent?.audio || "")
      ) {
        audioRef.current.src = matchedContent?.audio || "";
      }

      const handlePlay = () => {
        if (audioRef.current && videoRef.current) {
          // Sync audio currentTime with video
          if (
            Math.abs(
              audioRef.current.currentTime - videoRef.current.currentTime
            ) > 0.1
          ) {
            audioRef.current.currentTime = videoRef.current.currentTime;
          }
          audioRef.current.play();
        }
      };
      const handlePause = () => {
        if (audioRef.current) {
          audioRef.current.pause();
        }
      };
      const handleSeeked = () => {
        if (audioRef.current && videoRef.current) {
          audioRef.current.currentTime = videoRef.current.currentTime;
        }
      };
      const handleVideoEnded = () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      };

      videoRef.current.addEventListener("play", handlePlay);
      videoRef.current.addEventListener("pause", handlePause);
      videoRef.current.addEventListener("seeked", handleSeeked);
      videoRef.current.addEventListener("ended", handleVideoEnded);

      return () => {
        videoRef.current?.removeEventListener("play", handlePlay);
        videoRef.current?.removeEventListener("pause", handlePause);
        videoRef.current?.removeEventListener("seeked", handleSeeked);
        videoRef.current?.removeEventListener("ended", handleVideoEnded);
      };
    } else if (videoRef.current) {
      videoRef.current.muted = false;
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    }
  }, [step, matchedContent]);

  useEffect(() => {
    const processAndTrimVideo = async () => {
      if (!isDone || !recordedVideoUrl) return;
      if (Array.isArray(videoCapture) && videoCapture.length > 0) {
        await loadFFmpeg();
        setIsProcessingVideo(true);
        const ffmpeg = ffmpegRef.current;
        const inputFileName = "input.mp4";
        await ffmpeg.writeFile(
          inputFileName,
          await fetchFile(recordedVideoUrl)
        );
        const filesForUpload: File[] = [];
        const blobUrlsForPreview: string[] = [];
        for (let i = 0; i < videoCapture.length; i++) {
          const timeRange = videoCapture[i];
          const [start, end] = timeRange.split("-").map(parseFloat);
          const outputFileName = `output-${i}.mp4`;
          await ffmpeg.exec([
            "-ss",
            String(start),
            "-i",
            inputFileName,
            "-t",
            String(end - start),
            "-c:v",
            "libx264",
            "-c:a",
            "aac",
            "-pix_fmt",
            "yuv420p",
            "-preset",
            "ultrafast",
            "-crf",
            "30",
            "-tune",
            "fastdecode",
            outputFileName,
          ]);
          const data = await ffmpeg.readFile(outputFileName);
          if (data instanceof Uint8Array) {
            const fileBlob = new Blob([new Uint8Array(data)], {
              type: "video/mp4",
            });
            blobUrlsForPreview.push(URL.createObjectURL(fileBlob));
            const trimmedFile = new File(
              [fileBlob],
              `exercise-segment-${timeRange}.mp4`,
              { type: "video/mp4" }
            );
            filesForUpload.push(trimmedFile);
          }
        }
        setVideoFiles(filesForUpload);
        setTrimmedVideoBlobUrls(blobUrlsForPreview);
        setIsProcessingVideo(false);
        toast.success(t("toast.trimmingSuccess"), { id: "video-trim" });
      } else {
        const response = await fetch(recordedVideoUrl);
        const blob = await response.blob();
        const file = new File([blob], "exercise-video.mp4", {
          type: blob.type,
        });
        setVideoFiles([file]);
        setTrimmedVideoBlobUrls([URL.createObjectURL(blob)]);
      }
    };
    processAndTrimVideo();
    return () => {
      trimmedVideoBlobUrls.forEach(URL.revokeObjectURL);
    };
  }, [isDone, recordedVideoUrl, videoCapture, t]);

  const isFinishDisabled =
    isRecording ||
    isProcessingVideo ||
    isUploading ||
    (isDone && videoFiles.length === 0 && videoCapture.length > 0);

  if (isMobile && isLandscape && step === "recording") {
    if (!exercise) return null;
    return (
      <LandscapeModeUI
        videoRef={videoRef}
        videoSrc={exercise?.video || ""}
        cameraVideoRef={cameraVideoRef}
        currentRep={currentRep}
        countReps={countReps}
        currentSet={currentSet}
        countSets={countSets}
        isResting={isResting}
        onFinish={handleFinish}
        onRestart={handleRestart}
        isFinishDisabled={isFinishDisabled}
        t={t}
        restProgress={restProgress}
        timeBetweenSets={timeBetweenSets}
        isUploading={isUploading}
      />
    );
  }

  if (!exercise) return null;

  return (
    <div className="relative flex justify-center px-0 md:px-4 h-full mb-20 lg:mb-0">
      {/* Blurred overlay when modal open */}
      {contraModalOpen && (
        <div
          style={{ zIndex: 1200 }}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300"
        />
      )}
      <div className="w-full max-w-5xl md:mt-1 relative z-10">
        {/* Contraindications Modal */}
        <ContraindicationsModal
          open={contraModalOpen}
          onClose={handleContraDisagree}
          onAgree={handleContraAgree}
          isContraScrollable={isContraScrollable}
          isContraScrolledToEnd={isContraScrolledToEnd}
          contraDialogContentRef={contraDialogContentRef}
          onScroll={handleContraScroll}
          contraindications={
            Array.isArray(exercise?.contraindications)
              ? exercise.contraindications.map((c: any, idx: number) => ({
                  id: idx,
                  content: c,
                }))
              : []
          }
          t={t}
        />
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            className="mr-3 text-orange-500 hover:bg-orange-100 rounded-full p-2 transition"
            onClick={() => {
              handleBack();
            }}
            aria-label="Back"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-gray-700">
            {matchedContent?.name || "--"}
          </h2>
        </div>
        <div className="bg-white rounded-lg shadow-md p-2 md:p-2 flex flex-col items-center text-center space-y-4">
          {step === "init" && (
            <div className="w-full max-w-xs md:max-w-[580px] flex items-center justify-center px-4 py-3 mb-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800">
              <Info className="w-5 h-5 mr-2" />
              <span className="text-sm">{t("alert.rotate")}</span>
            </div>
          )}
          <video
            ref={videoRef}
            src={
              step === "init"
                ? matchedContent?.video || "--"
                : exercise?.video || "--"
            }
            controls
            muted
            playsInline
            loop={step === "recording" && !isResting && !isDone}
            className="w-full max-w-xs md:max-w-[580px] h-[180px] md:h-[280px] object-cover rounded-md"
            style={{ display: "block" }}
          />
          {/* Audio element for playback during exercise */}
          <audio
            ref={audioRef}
            src={matchedContent?.audio || ""}
            style={{ display: "none" }}
          />
          {step === "init" && (
            <>
              <div className="w-full max-w-xs md:max-w-[580px]">
                <h2 className="text-md font-bold text-gray-700 text-left mb-2">
                  {t("appointment.explanation")}
                </h2>
                <div className="text-md text-left text-gray-800">
                  {matchedContent?.explanation || "<p>--</p>"}{" "}
                </div>
              </div>
              <button
                className=" bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg shadow transition text-base md:text-lg"
                onClick={handleStartPreview}
              >
                {t("button.previewCamera")}
              </button>
              {error && <div className="text-red-500 text-sm">{error}</div>}
            </>
          )}
          {step === "preview" && (
            <>
              <div className="w-full max-w-xs md:max-w-[580px] h-[180px] md:h-[280px] bg-black rounded-md flex items-center justify-center mb-4 relative">
                {stream ? (
                  <>
                    <video
                      autoPlay
                      playsInline
                      muted
                      ref={previewVideoRef}
                      className="w-full h-full object-cover rounded-md"
                    />
                    {countdown !== null && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-10">
                        <span className="text-orange-500 text-6xl font-bold">
                          {countdown}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-white text-xs md:text-base">
                    {t("alert.cameraAccess")}
                  </span>
                )}
              </div>
              <button
                className={`px-6 py-2 rounded-lg shadow transition text-base md:text-lg ${
                  !stream || countdown !== null
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-orange-500 hover:bg-orange-600 text-white"
                }`}
                onClick={
                  countdown === null && stream
                    ? handleStartRecording
                    : undefined
                }
                disabled={!stream || countdown !== null}
              >
                {t("button.startExercise")}
              </button>
            </>
          )}
          {step === "recording" && (
            <>
              {!isDone && (
                <div className="w-full max-w-xs md:max-w-[580px] h-[180px] md:h-[280px] bg-black rounded-md flex items-center justify-center mb-4">
                  {stream ? (
                    <video
                      autoPlay
                      playsInline
                      muted
                      ref={cameraVideoRef}
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <span className="text-white">
                      {t("alert.cameraAccess")}
                    </span>
                  )}
                </div>
              )}
              <div className="w-full max-w-xs md:max-w-[580px] h-auto">
                {isDone ? (
                  <>
                    <p className="text-orange-600 text-center text-lg font-semibold">
                      {t("label.done") || "Exercise completed!"}
                    </p>
                    {(isProcessingVideo || isUploading) && (
                      <div className="flex flex-col items-center justify-center h-[180px] md:h-[280px]">
                        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                        <p className="mt-2 text-gray-600">
                          {isProcessingVideo
                            ? t("toast.processingVideo")
                            : t("toast.uploadingVideo")}
                        </p>
                      </div>
                    )}
                    {trimmedVideoBlobUrls.length > 0 &&
                      !isProcessingVideo &&
                      !isUploading && (
                        <>
                          {trimmedVideoBlobUrls.length === 1 ? (
                            // Single video layout
                            <div className="w-full max-w-xs md:max-w-[580px] mt-4">
                              <p className="text-sm text-gray-600 mb-1 text-left">
                                {t("label.videoCapture")} 1
                              </p>
                              <video
                                src={trimmedVideoBlobUrls[0]}
                                controls
                                className="w-full h-auto rounded-md"
                              />
                            </div>
                          ) : (
                            // Multiple videos layout
                            <div className="w-full flex flex-wrap justify-center gap-4 mt-4">
                              {trimmedVideoBlobUrls.map((url, index) => (
                                <div
                                  key={index}
                                  className="w-full sm:w-[calc(50%-0.5rem)]"
                                >
                                  <p className="text-sm text-gray-600 mb-1">
                                    {t("label.videoCapture")} {index + 1}
                                  </p>
                                  <video
                                    src={url}
                                    controls
                                    className="w-full h-auto rounded-md"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                  </>
                ) : isResting ? (
                  <>
                    <label className="text-left text-sm text-gray-600 font-semibold mb-1 block">
                      {`${Math.floor((restProgress * timeBetweenSets) / 100)}/${timeBetweenSets}`}{" "}
                      {t("label.resting")}
                    </label>
                    <div className="w-full h-3 bg-orange-100 rounded-full">
                      <div
                        className="h-3 bg-orange-400 rounded-full transition-all duration-300"
                        style={{ width: `${restProgress}%` }}
                      ></div>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="text-left text-sm text-gray-600 font-semibold mb-1 block">
                      {/* {`${currentRep}/${countReps}`}{" "} */}
                      {isDurationMode
                        ? `${currentRep}/${countReps} ${t("label.durationVideo") || "Duration Video"}`
                        : `${currentRep}/${countReps} ${t("label.repetitions") || "Repetitions"}`}
                    </label>
                    <div className="w-full h-3 bg-orange-100 rounded-full">
                      <div
                        className="h-3 bg-orange-500 rounded-full transition-all duration-300"
                        style={{ width: `${(currentRep / countReps) * 100}%` }}
                      ></div>
                    </div>

                    <label className="text-left text-sm text-gray-600 font-semibold mt-4 mb-1 block">
                      {`${currentSet}/${countSets}`} {t("label.sets") || "Sets"}
                    </label>
                    <div className="w-full h-3 bg-orange-100 rounded-full">
                      <div
                        className="h-3 bg-orange-500 rounded-full transition-all duration-300"
                        style={{ width: `${(currentSet / countSets) * 100}%` }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
              <div className="w-full flex flex-col md:flex-row items-center justify-center gap-4 mt-4 pt-4 border-t">
                <button
                  className={`w-full md:w-auto px-6 py-2 rounded-lg shadow transition text-base md:text-lg font-bold ${
                    isFinishDisabled
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600 text-white"
                  }`}
                  onClick={handleFinish}
                  disabled={isFinishDisabled}
                >
                  {t("button.finish") || "Finish"}
                </button>
                <button
                  className={`w-full md:w-auto px-6 py-2 rounded-lg shadow transition text-base md:text-lg font-bold ${
                    isUploading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                  }`}
                  onClick={handleRestart}
                  disabled={isUploading}
                >
                  {t("button.restart") || "Restart"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default ExerciseDetail;
